import type { LucideIcon } from "lucide-react";
import {
  Calendar,
  CheckSquare,
  ClipboardCheck,
  FileText,
  MapPin,
  PlayCircle,
  Scale,
  Users,
} from "lucide-react";
import type { OpenModal } from "./modals/useWorkshopModals";

/**
 * Workshop briefing content — what every workshop offers before the
 * session starts (intro video, pre-reading, pre-workshop questionnaire,
 * etc.). Ported in skeletal form from the `Brief` panel in
 * `_legacy/wd.html` (lines 68-110). Phase 2 (3/N) adds the optional
 * `modal` discriminator so each row knows which shared modal to open
 * (or in the intro-video case, which external URL to launch).
 */

export type BriefItemStatus = "available" | "complete" | "locked";

export type BriefItem = {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Right-side meta text (e.g. "▶ Play", "1 doc", "Complete"). */
  meta?: string;
  status?: BriefItemStatus;
  /**
   * Which shared modal this row opens when clicked. `null` rows fall
   * back to other behaviour (e.g. opening `videoUrl` in a new tab).
   */
  modal?: Exclude<OpenModal, null>;
  /**
   * External URL for the intro-video row. Opened with
   * `window.open(url, '_blank')`. In vanilla this came from
   * `WS4_SESSION.videoUrl` in `_legacy/ws4-session.js`; until that
   * per-workshop data is ported the BriefPanel composes a placeholder
   * from the workshop id.
   */
  videoUrl?: string;
};

/**
 * Default brief items used by every workshop until per-workshop
 * content is filled in. Pre-session launchers include the questionnaire
 * *survey*; facilitation-side questionnaire *results* live in Asset
 * Locker → Outputs.
 *
 * Per-workshop entries in `briefByWorkshop` can extend this list.
 */
const DEFAULT_BRIEF: BriefItem[] = [
  {
    id: "intro-video",
    label: "XAES Intro Video",
    icon: PlayCircle,
    meta: "▶ Play",
    status: "available",
  },
  {
    id: "pre-reading",
    label: "Pre-reading",
    icon: FileText,
    meta: "1 doc",
    status: "available",
    modal: "doc",
  },
  {
    // No authored meta/status — the BriefPanel derives "n/N answered" /
    // Complete live from `useQuestionnaires`, so the row reflects what
    // the respondent actually filled in (UAT: it used to hardcode
    // "Complete").
    id: "questionnaire",
    label: "Questionnaire",
    icon: ClipboardCheck,
    modal: "questionnaire",
  },
  {
    id: "agenda",
    label: "Workshop Agenda",
    icon: Calendar,
    modal: "agenda",
  },
  {
    id: "logistics",
    label: "Logistics",
    icon: MapPin,
    modal: "map",
  },
  {
    id: "bring",
    label: "What to Bring",
    icon: CheckSquare,
    modal: "bring",
  },
  {
    id: "meet-crew",
    label: "Meet the Crew",
    icon: Users,
    modal: "meet-crew",
  },
  {
    id: "decision-log",
    label: "Decisions, Actions & Assumptions",
    icon: Scale,
    modal: "decision-log",
  },
];

// Every workshop offers the same standard Brief launchers. Questionnaire
// results are an Asset Locker Output (not Brief). Each workshop gets its
// OWN array copy so a per-workshop extension can append without mutating
// others.
export const briefByWorkshop: Record<string, BriefItem[]> = {
  WS1: [...DEFAULT_BRIEF],
  WS2: [...DEFAULT_BRIEF],
  WS3: [...DEFAULT_BRIEF],
  WS4: [...DEFAULT_BRIEF],
  WS5: [...DEFAULT_BRIEF],
  WS6: [...DEFAULT_BRIEF],
  WS7: [...DEFAULT_BRIEF],
  WS8: [...DEFAULT_BRIEF],
};
